import React, { FC, useCallback, useMemo } from "react";
import { ChartConfig, ChartContainer } from "@/components/ui/chart";
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
export interface AnalyticsChartRow {
  category: string;
  occupancy: number;
  guestSatRaw: number;
  guestSatScaled: number;
  upsells: number;
}

const OCC_STROKE = "#C9A962";
const OCC_FILL_TOP = "rgba(201, 169, 98, 0.45)";
const GS_STROKE = "#A78BFA";
const GS_FILL_TOP = "rgba(167, 139, 250, 0.4)";
const UP_STROKE = "#6D28D9";
const UP_FILL_TOP = "rgba(109, 40, 217, 0.42)";

type TooltipContentProps = {
  active?: boolean;
  label?: string;
  payload?: ReadonlyArray<{ payload: AnalyticsChartRow }>;
  showGuestSatisfaction?: boolean;
  showUpsells?: boolean;
};

const AnalyticsTooltipContent: FC<TooltipContentProps> = ({
  active,
  label,
  payload,
  showGuestSatisfaction = true,
  showUpsells = true,
}) => {
  if (!active || !payload?.length || label === undefined) {
    return null;
  }
  const row = payload[0].payload;
  const title = String(label);

  return (
    <div className="min-w-[220px] rounded-xl border border-zinc-100 bg-white px-3 py-2.5 text-xs shadow-lg">
      <div className="mb-2 font-semibold text-neutral-800">{title}</div>
      <div className="flex flex-col gap-2.5">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2">
            <span
              className="size-2 shrink-0 rounded-full"
              style={{ backgroundColor: OCC_STROKE }}
              aria-hidden
            />
            <span className="text-neutral-600">Occupancy</span>
          </div>
          <span className="font-semibold tabular-nums text-neutral-900">
            {Math.round(row.occupancy)}%
          </span>
        </div>
        {showGuestSatisfaction ? (
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-2">
              <span
                className="size-2 shrink-0 rounded-full"
                style={{ backgroundColor: GS_STROKE }}
                aria-hidden
              />
              <span className="text-neutral-600">Guest Satisfaction</span>
            </div>
            <div className="text-right">
              <span
                className="font-semibold tabular-nums"
                style={{ color: GS_STROKE }}
              >
                {row.guestSatRaw < 0 ? "—" : row.guestSatRaw.toFixed(1)}
              </span>
              <span className="text-neutral-500">/5</span>
            </div>
          </div>
        ) : null}
        {showUpsells ? (
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-2">
              <span
                className="size-2 shrink-0 rounded-full"
                style={{ backgroundColor: UP_STROKE }}
                aria-hidden
              />
              <span className="text-neutral-600">Upsells</span>
            </div>
            <span className="font-semibold tabular-nums text-neutral-900">
              {Math.round(row.upsells)}
            </span>
          </div>
        ) : null}
      </div>
    </div>
  );
};

const analyticsChartConfig: ChartConfig = {
  occupancy: { label: "Occupancy", color: OCC_STROKE },
  guestSat: { label: "Guest Satisfaction", color: GS_STROKE },
  upsells: { label: "Upsells", color: UP_STROKE },
};

export interface CategoryActivityChartProps {
  data: AnalyticsChartRow[];
  showGuestSatisfaction?: boolean;
  showUpsells?: boolean;
}

export const CategoryActivityChart: FC<CategoryActivityChartProps> = ({
  data,
  showGuestSatisfaction = true,
  showUpsells = true,
}) => {
  const tooltipContent = useCallback(
    (props: {
      active?: boolean;
      label?: string;
      payload?: ReadonlyArray<{ payload: AnalyticsChartRow }>;
    }) => (
      <AnalyticsTooltipContent
        {...props}
        showGuestSatisfaction={showGuestSatisfaction}
        showUpsells={showUpsells}
      />
    ),
    [showGuestSatisfaction, showUpsells],
  );

  const defs = useMemo(
    () => (
      <defs>
        <linearGradient id="analytics-occ-fill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={OCC_FILL_TOP} />
          <stop offset="100%" stopColor={OCC_STROKE} stopOpacity={0} />
        </linearGradient>
        <linearGradient id="analytics-gs-fill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={GS_FILL_TOP} />
          <stop offset="100%" stopColor={GS_STROKE} stopOpacity={0} />
        </linearGradient>
        <linearGradient id="analytics-up-fill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={UP_FILL_TOP} />
          <stop offset="100%" stopColor={UP_STROKE} stopOpacity={0} />
        </linearGradient>
      </defs>
    ),
    [],
  );

  const areaCommon = {
    type: "monotone" as const,
    isAnimationActive: false,
    strokeWidth: 2,
    dot: false,
    activeDot: { r: 5, strokeWidth: 2, stroke: "#fff" },
  };

  return (
    <ChartContainer
      config={analyticsChartConfig}
      className="min-h-0 w-full flex-1 !aspect-auto font-geist"
    >
      <ComposedChart
        data={data}
        margin={{ top: 12, right: 8, left: 8, bottom: 4 }}
        accessibilityLayer
      >
        {defs}
        <CartesianGrid vertical={false} stroke="#F4F4F5" strokeWidth={1} />
        <XAxis
          dataKey="category"
          type="category"
          tickLine={false}
          axisLine={false}
          tickMargin={8}
          tick={{ fill: "#A3A3A3", fontSize: 12 }}
        />
        <YAxis domain={[0, 100]} hide />
        <Tooltip
          cursor={{ stroke: "#E4E4E7", strokeWidth: 1 }}
          isAnimationActive={false}
          allowEscapeViewBox={{ x: true, y: true }}
          wrapperStyle={{ zIndex: 50 }}
          content={tooltipContent}
        />
        {showUpsells ? (
          <Area
            dataKey="upsells"
            stroke={UP_STROKE}
            fill="url(#analytics-up-fill)"
            {...areaCommon}
          />
        ) : null}
        {showGuestSatisfaction ? (
          <Area
            dataKey="guestSatScaled"
            stroke={GS_STROKE}
            fill="url(#analytics-gs-fill)"
            {...areaCommon}
          />
        ) : null}
        <Area
          dataKey="occupancy"
          stroke={OCC_STROKE}
          fill="url(#analytics-occ-fill)"
          {...areaCommon}
        />
      </ComposedChart>
    </ChartContainer>
  );
};
